/**
 * Applies a station-pipeline result to already-loaded X3A scans.
 *
 * The pipeline itself runs in the extension host, inside a fresh parse, because
 * that is the only moment the full-resolution X3I frames exist — only a
 * 1/8-scale preview survives into the webview, and a preview pixel spans about
 * thirteen points vertically, which would visibly smear the colour.
 *
 * What comes back is deliberately *not* a new set of clouds: re-sending 26
 * million points to replace points that did not move would cost far more than
 * the result is. Only two things travel — a 4x4 per scan, and new colours for
 * the scans whose colour actually changed, which in the default "fill in the
 * grey ones" mode is just the small preview sweeps.
 */
import * as THREE from 'three';
import { RECOLORED_MODE } from './colorMode';
import { filesState } from './state/files.svelte';
import { stationPipelineUi } from './state/registration.svelte';
import { noteContainerScanLoaded } from './utils/containerPerf';
import { addStonexCameraVisualization, updateStonexCameraStations, } from './visualization/stonexCameras';
import { applyStonexColorCorrectionToPoints, computeStonexFrameMultipliers, DEFAULT_STONEX_COLOR_CORRECTION, } from './visualization/stonexColorCorrection';
/**
 * The scan's cross-station colour buffer, created white on first use.
 *
 * White rather than a copy of the native colour, so selecting the mode before
 * the result lands shows plainly which points have been reached and which have
 * not — and so a scan that was grey to begin with does not silently look
 * finished.
 */
function ensureRecoloredArray(data) {
    const metadata = (data.metadata ?? (data.metadata = {}));
    const existing = metadata.stationRecoloredColors;
    const count = data.vertexCount || (data.positionsArray?.length ?? 0) / 3;
    if (existing instanceof Uint8Array && existing.length === count * 3) {
        return existing;
    }
    const created = new Uint8Array(count * 3).fill(255);
    metadata.stationRecoloredColors = created;
    return created;
}
/** Switches a file to the recoloured mode if it is not already on it. */
function selectRecoloredMode(host, fileIndex) {
    if (host.individualColorModes?.[fileIndex] === RECOLORED_MODE) {
        return;
    }
    host.onFileColorModeChange?.(fileIndex, RECOLORED_MODE);
    filesState.colorModes[fileIndex] = RECOLORED_MODE;
}
/** Pushes the recoloured buffer at the GPU when it is what is being drawn. */
function refreshRecoloredAttribute(host, fileIndex, data) {
    const geometry = host.meshes[fileIndex]?.geometry;
    const attribute = geometry?.getAttribute('color');
    const recolored = data.metadata?.stationRecoloredColors;
    if (attribute && attribute.array === recolored) {
        attribute.needsUpdate = true;
    }
}
export function applyStationPipelineUpdates(host, updates) {
    const outcome = { placed: 0, recolored: 0 };
    for (const update of updates) {
        const fileIndex = host.spatialFiles.findIndex(data => data?.metadata?.embeddedScanName === update.scanName);
        if (fileIndex < 0) {
            continue;
        }
        const data = host.spatialFiles[fileIndex];
        if (update.transform && update.transform.length === 16) {
            // Absolute, not composed: the pipeline expresses every scan relative to
            // the same anchor, so re-running it must not stack onto the previous run.
            host.setTransformationMatrix(fileIndex, new THREE.Matrix4().fromArray(update.transform));
            host.updateMatrixTextarea(fileIndex);
            outcome.placed++;
        }
        if (!update.rawColors || !update.frameIndices) {
            continue;
        }
        const metadata = (data.metadata ?? (data.metadata = {}));
        // Into its own array, never over `colorsArray`: the scan's native colour
        // has to stay switchable, and comparing the two is the point.
        const recolored = ensureRecoloredArray(data);
        // A partial update carries only this scan's points, which is the whole
        // archive's array sliced to its range; a full one replaces everything.
        const target = update.partial && update.rawColors.length < recolored.length
            ? recolored.subarray(0, update.rawColors.length)
            : recolored;
        const calibration = metadata.stonexColorCalibration;
        const correction = metadata.stonexColorCorrection ??
            DEFAULT_STONEX_COLOR_CORRECTION;
        if (calibration) {
            applyStonexColorCorrectionToPoints(update.rawColors, update.frameIndices, computeStonexFrameMultipliers(calibration, correction), correction, target);
        }
        else {
            target.set(update.rawColors);
        }
        metadata.stationRecoloredRaw = update.rawColors;
        metadata.stationRecoloredFrames = update.frameIndices;
        if (typeof update.photographicallyColoredPoints === 'number') {
            metadata.stationRecoloredPoints = update.photographicallyColoredPoints;
        }
        // Show it straight away — a result you have to go and find is a result most
        // people never see.
        const pending = pendingRecolor.get(fileIndex);
        if (pending) {
            pending.filled = true;
        }
        selectRecoloredMode(host, fileIndex);
        refreshRecoloredAttribute(host, fileIndex, data);
        // The host publishes each scan as it finishes, so ticking the row here is a
        // report of what has actually landed rather than an estimate.
        const progressRow = stationPipelineUi.scans.find(scan => scan.name === update.scanName);
        if (progressRow) {
            progressRow.colored = true;
        }
        outcome.recolored++;
    }
    // Panoramas live in their own station's frame, so they move with the scans.
    if (host.cameraGroups?.length) {
        updateStonexCameraStations(host);
    }
    // Rebuild the rows. The colour picker's "camera (all stations)" entry is
    // gated on renderTick, while the *selected* mode is written straight into a
    // reactive array - without this the select ends up pointing at an option that
    // was never rendered, which shows as a blank picker with no way back.
    host.updateFileList?.();
    host.updateFileStats?.();
    host.requestRender();
    return outcome;
}
/** Progress line from the host's run, shown while the button is busy. */
export function reportStationPipelineProgress(message) {
    if (typeof message === 'string' && message.length > 0) {
        stationPipelineUi.message = message;
    }
}
/**
 * Paints the archive's scans white and switches them to the camera view, before
 * any result exists.
 *
 * The point is to make the run legible: the scans go blank, then fill in as the
 * host reports each one. Without it the view sits unchanged for a minute and
 * flips in a single step, which reads as "nothing is happening".
 */
/**
 * Scans blanked for the current run, with the colour mode each had beforehand.
 *
 * Needed because a run can legitimately colour nothing: a single-station
 * archive has no other camera to offer, so "all stations" is the same view it
 * already had. Those scans have to be handed back rather than left white.
 */
const pendingRecolor = new Map();
export function beginStationRecolor(host, archiveName, preserveExistingColor = true, scopeScanStems) {
    pendingRecolor.clear();
    const scope = scopeScanStems?.length ? new Set(scopeScanStems) : null;
    // Rebuilt from scratch: the progress list belongs to this run, and a leftover
    // row from the previous one would read as work that is not happening.
    const scanOrder = [];
    let prepared = 0;
    for (let index = 0; index < host.spatialFiles.length; index++) {
        const data = host.spatialFiles[index];
        if (data?.metadata?.containerFileName !== archiveName || !data.metadata?.embeddedScanName) {
            continue;
        }
        const stem = String(data.metadata.embeddedScanName).replace(/\.x3r$/i, '');
        if (scope && !scope.has(stem)) {
            continue;
        }
        const recolored = ensureRecoloredArray(data);
        if (preserveExistingColor && data.colorsArray) {
            // “Do not recolour existing points” should be visible immediately too:
            // seed the synthetic view with their exact current display colours rather
            // than blanking them white until the host sends the same pixels back.
            recolored.set(data.colorsArray.subarray(0, recolored.length));
        }
        else {
            recolored.fill(255);
        }
        pendingRecolor.set(index, {
            previousMode: host.individualColorModes?.[index] ?? 'original',
            filled: false,
        });
        scanOrder.push({ index, name: String(data.metadata.embeddedScanName) });
        selectRecoloredMode(host, index);
        refreshRecoloredAttribute(host, index, data);
        prepared++;
    }
    stationPipelineUi.scans = scanOrder.map(scan => ({ name: scan.name, colored: false }));
    if (prepared > 0) {
        host.updateFileList?.();
        host.requestRender();
    }
    return prepared;
}
/**
 * Completes every scan the run had nothing to add to.
 *
 * "All stations" is a view, not a diff, so it has to exist for every scan of
 * the archive — including the ones no other camera could improve. For those the
 * answer is already on hand: with nothing else reaching them, the all-stations
 * result *is* their own station's colour, so it is copied locally rather than
 * shipped back across a postMessage that would carry tens of megabytes to say
 * nothing new.
 *
 * A scan with no colour at all is the one case with nothing to copy; that one
 * goes back to the mode it came from rather than sitting white.
 */
function completeUntouchedScans(host) {
    let filled = 0;
    let released = 0;
    for (const [fileIndex, pending] of pendingRecolor) {
        if (pending.filled) {
            continue;
        }
        const data = host.spatialFiles[fileIndex];
        const recolored = data?.metadata?.stationRecoloredColors;
        if (data?.colorsArray && recolored instanceof Uint8Array) {
            recolored.set(data.colorsArray.subarray(0, recolored.length));
            refreshRecoloredAttribute(host, fileIndex, data);
            filled++;
            continue;
        }
        if (data?.metadata) {
            delete data.metadata.stationRecoloredColors;
        }
        host.onFileColorModeChange?.(fileIndex, pending.previousMode);
        filesState.colorModes[fileIndex] = pending.previousMode;
        released++;
    }
    pendingRecolor.clear();
    return { filled, released };
}
/** Entry point for the `stationPipelineResult` message from the host. */
export function handleStationPipelineResult(host, message) {
    if (message.partial) {
        // Progress, not the end of the run: paint it and leave the button busy.
        applyStationPipelineUpdates(host, message.updates ?? []);
        return;
    }
    stationPipelineUi.busy = false;
    const untouched = completeUntouchedScans(host);
    // A scan no camera could improve never produces an update, so without this
    // the progress list would sit at "5 of 6" after a run that is genuinely
    // finished. On an error the rows stay as they were - unfinished, which is
    // what happened.
    if (!message.error) {
        for (const scan of stationPipelineUi.scans) {
            scan.colored = true;
        }
    }
    if (message.error) {
        stationPipelineUi.message = `Station pipeline failed: ${message.error}`;
        return;
    }
    // Applying touches geometry and the file list, so a failure here must land
    // in front of the user rather than as an unhandled rejection in a listener.
    try {
        const outcome = applyStationPipelineUpdates(host, message.updates ?? []);
        stationPipelineUi.message =
            `${message.summary ?? 'Done'} · placed ${outcome.placed} scans` +
                (outcome.recolored > 0 ? `, recoloured ${outcome.recolored}` : '') +
                (untouched.filled > 0
                    ? ` · ${untouched.filled} unchanged (no other station reaches them)`
                    : '') +
                (untouched.released > 0 ? ` · ${untouched.released} had no colour to show` : '');
    }
    catch (error) {
        console.error('[station-pipeline] applying the result failed:', error);
        stationPipelineUi.message = `Could not apply the result: ${error instanceof Error ? error.message : String(error)}`;
    }
}
/** Two origins within this distance are treated as one tripod position. */
const SAME_PLACE_METRES = 0.5;
/**
 * Groups an archive's scans by where they were actually captured.
 *
 * Clustering registered origins rather than reading a field, because no field
 * exists: an X3A says nothing about which sweep belongs to which setup. After
 * registration the answer is obvious — a station's quick preview sweeps land on
 * top of its full scan, metres away from the next tripod position — and before
 * registration everything sits on the origin and honestly is one group.
 */
export function capturePlaces(host, archiveName) {
    if (!archiveName) {
        return [];
    }
    const places = [];
    const photographic = new Set();
    for (const data of host.spatialFiles) {
        for (const frame of (data?.metadata?.stonexCameraFrames ?? [])) {
            if (frame.scanStem) {
                photographic.add(frame.scanStem);
            }
        }
    }
    for (let index = 0; index < host.spatialFiles.length; index++) {
        const metadata = host.spatialFiles[index]?.metadata;
        if (metadata?.containerFileName !== archiveName || !metadata?.embeddedScanName) {
            continue;
        }
        const stem = String(metadata.embeddedScanName).replace(/\.x3r$/i, '');
        const origin = new THREE.Vector3().setFromMatrixPosition(host.transformationMatrices[index]);
        const existing = places.find(place => place.origin.distanceTo(origin) <= SAME_PLACE_METRES);
        const place = existing ??
            (places.push({ name: stem, fileIndices: [], scanStems: [], origin }),
                places[places.length - 1]);
        place.fileIndices.push(index);
        place.scanStems.push(stem);
        // A station is best named after the sweep its cameras belong to.
        if (photographic.has(stem)) {
            place.name = stem;
        }
    }
    return places.map(({ name, fileIndices, scanStems }) => ({ name, fileIndices, scanStems }));
}
/** Shows or hides everything captured from one place, cameras included. */
export function setCapturePlaceVisible(host, place, visible) {
    for (const fileIndex of place.fileIndices) {
        host.setFileEntryVisibility(fileIndex, visible);
    }
    const stems = new Set(place.scanStems);
    for (const profile of host.cameraGroups ?? []) {
        for (const child of profile.children) {
            const stem = child.userData?.frame?.scanStem ??
                child.userData?.stationStem;
            if (stem && stems.has(stem)) {
                child.visible = visible;
            }
        }
    }
    host.requestRender();
}
// Extension messages can overtake an async geometry build in the webview. Keep
// bounded colour chunks until their scan exists instead of silently dropping
// them; the binary and large-file completion paths flush this map.
const pendingLoadTimeColors = new Map();
/**
 * Attaches the photographic colour to scans that were put on screen without it.
 *
 * The load hands geometry over first because colour is most of the work; this
 * is the other half arriving. Switching the mode is what makes it visible - the
 * clouds went up with no colour attribute at all, so they are showing their
 * assigned flat colour until now.
 */
export function applyLoadTimeColors(host, updates) {
    let applied = 0;
    for (const update of updates) {
        const fileIndex = host.spatialFiles.findIndex(data => data?.metadata?.embeddedScanName === update.scanName);
        if (fileIndex < 0 || !update.colors) {
            if (fileIndex < 0 && update.scanName && update.colors) {
                const queued = pendingLoadTimeColors.get(update.scanName) ?? [];
                queued.push(update);
                pendingLoadTimeColors.set(update.scanName, queued);
            }
            continue;
        }
        const data = host.spatialFiles[fileIndex];
        const metadata = (data.metadata ?? (data.metadata = {}));
        const chunked = typeof update.pointOffset === 'number';
        if (!chunked) {
            data.colorsArray = update.colors;
            metadata.stonexRawColors = update.colorsAreRaw
                ? update.colors.slice()
                : (update.rawColors ?? null);
            metadata.stonexFrameIndices = update.frameIndices ?? null;
        }
        else {
            // Grow the destination once, then fill it chunk by chunk. The arrays are
            // sized from the scan's own point count rather than from what has arrived
            // so far, so an interrupted transfer leaves a partly coloured scan rather
            // than a mismatched one.
            const points = update.totalPoints ?? data.vertexCount;
            if (!(data.colorsArray instanceof Uint8Array) || data.colorsArray.length !== points * 3) {
                data.colorsArray = new Uint8Array(points * 3);
            }
            data.colorsArray.set(update.colors, update.pointOffset * 3);
            if (update.rawColors) {
                if (!(metadata.stonexRawColors instanceof Uint8Array)) {
                    metadata.stonexRawColors = new Uint8Array(points * 3);
                }
                metadata.stonexRawColors.set(update.rawColors, update.pointOffset * 3);
            }
            if (update.frameIndices) {
                if (!(metadata.stonexFrameIndices instanceof Uint16Array)) {
                    metadata.stonexFrameIndices = new Uint16Array(points);
                }
                metadata.stonexFrameIndices.set(update.frameIndices, update.pointOffset);
            }
            if (!update.final) {
                continue;
            }
        }
        data.hasColors = true;
        if (update.colorCalibration !== undefined) {
            metadata.stonexColorCalibration = update.colorCalibration;
        }
        if (typeof update.photographicallyColoredPoints === 'number') {
            metadata.photographicallyColoredPoints = update.photographicallyColoredPoints;
        }
        if (update.colorsAreRaw && data.colorsArray && metadata.stonexFrameIndices) {
            const raw = data.colorsArray.slice();
            metadata.stonexRawColors = raw;
            const calibration = metadata.stonexColorCalibration;
            if (calibration) {
                applyStonexColorCorrectionToPoints(raw, metadata.stonexFrameIndices, computeStonexFrameMultipliers(calibration, DEFAULT_STONEX_COLOR_CORRECTION), DEFAULT_STONEX_COLOR_CORRECTION, data.colorsArray);
            }
        }
        // Camera previews are sampled from the decoded photographs, so the geometry
        // hand-over cannot carry them - they arrive here with the colour, and the
        // profile is built now rather than at load.
        if (update.cameraFrames?.length) {
            metadata.stonexCameraFrames = update.cameraFrames;
            addStonexCameraVisualization(host, data);
        }
        host.onFileColorModeChange?.(fileIndex, 'original');
        filesState.colorModes[fileIndex] = 'original';
        if (!chunked || update.final) {
            noteContainerScanLoaded(update.container, data.vertexCount);
        }
        applied++;
    }
    if (applied > 0) {
        host.updateFileList?.();
        host.requestRender();
    }
    return applied;
}
/** Applies colour chunks that arrived while their geometry was still building. */
export function flushPendingLoadTimeColors(host) {
    let applied = 0;
    for (const [scanName, updates] of [...pendingLoadTimeColors]) {
        if (!host.spatialFiles.some(data => data?.metadata?.embeddedScanName === scanName)) {
            continue;
        }
        pendingLoadTimeColors.delete(scanName);
        applied += applyLoadTimeColors(host, updates);
    }
    return applied;
}
